import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const CostaRicaPage = () => {
  const costaRicaData = {
    name: 'Costa Rica',
    region: 'Latin America',
    color: '#C85A54',
    metaTitle: 'Pharma & Nutraceutical Registration in Costa Rica | MoH Costa Rica | CTD & Supplement Rules',
    metaDescription: 'Understand how to register pharmaceuticals and nutraceuticals in Costa Rica. MoH requirements, CTD/recognition frameworks, supplement classification, required docs and why Costa Rica is a strategic Central-American entry point.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Costa Rica',
      subtitle: 'A stable Central American economy with modernising regulatory pathways—including recognition of ICH-country approvals—and growing wellness demand; ideal for exporters with quality dossiers and clear strategy.',
      cta: 'Start Your Costa Rica Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~5.2 million (2024 est)' },
      { label: 'Income Level', value: 'Upper-middle income; high healthcare access for region' },
      { label: 'Regulator', value: 'Ministerio de Salud (MoH) – Medicines and health-products registration' },
      { label: 'Key Regulatory Reform', value: 'Executive Decree recognising ICH approvals (December 2022)' },
      { label: 'Supplement Regulation', value: 'Dietary supplements and natural medicinal-products must be registered before first sale' },
      { label: 'Strategic Value', value: 'Gateway to Central America with manageable market size' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Gateway to Central America + Competitive Entry',
      stats: [
        'Pharmaceuticals, drugs, cosmetics and medical devices must be registered with the MoH; import permits required',
        'Executive Decree (Dec 2022) allows MoH to recognise marketing approvals from ICH-member authorities (US FDA, EMA)',
        'Recognition decree offers accelerated route for products with reference-market approvals',
        'Dietary supplements and natural medicinal products require registration under local sanitary regulation',
        'Market smaller than major LatAm markets, but offers regulatory clarity and strategic location',
        'Valuable first step for Central-American and Caribbean region expansion'
      ],
      opportunities: [
        'Prioritise products with reference-market approvals (US/EU) to leverage recognition route',
        'Recognition pathway does not eliminate local registration documentation requirements',
        'Use Costa Rica as hub for Central America - both local market and regional export strategy',
        'Manageable regulatory environment ideal for building regional presence'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Wellness Growth & Chronic Demand',
      subtitle: 'Rising prevalence of overweight, obesity and NCDs in Central America drives demand for chronic-care medicines and wellness supplements.',
      categories: [
        {
          name: 'Non-Communicable Diseases & Metabolic Health',
          items: [
            'Rising overweight and obesity rates across Central America region',
            'Increasing NCD burden (diabetes, hypertension, cardiovascular disease)',
            'Chronic disease management driving pharmaceutical demand',
            'Regional health sector requires registration for dietary supplements and natural products',
            'Pharma demand: Antidiabetics, antihypertensives, lipid-lowering agents',
            'Pharma demand: Cardio-renal therapies, chronic-care management',
            'Growing awareness of preventive health and wellness'
          ]
        },
        {
          name: 'Wellness & Supplement Market',
          items: [
            'Dietary supplements subject to registration before sale',
            'Natural medicinal products governed by Central American Technical Regulation',
            'Premium supplements gaining traction in urban centers',
            'Health-conscious consumers seeking quality wellness products',
            'Nutra opportunities: Metabolic health supplements',
            'Nutra opportunities: Cardiovascular support, vitamins/minerals',
            'Wellness-oriented formats with clear classification and claims'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Ministry of Health (Ministerio de Salud - MoH)',
      description: 'MoH is the main regulatory body for medicines, health-products and food supplements in Costa Rica. Key reform in December 2022: MoH issued decree recognizing marketing approvals from ICH regulatory authorities for pharmaceuticals, enabling faster local registration when conditions are met. Dietary supplements and natural medicinal products require registration before sale per Central American sanitary regulations.',
      pharma: {
        timeline: 'Timelines depend on whether recognition route is used (which may be faster) or full dossier route is required. Recognition route offers accelerated review if ICH-market approval criteria are met. Full dossier submissions may take standard registration timeline of several months. Exact timelines vary based on dossier quality and regulator workload.',
        requirements: [
          'Foreign manufacturers must appoint legal representative or local importer authorised by MoH',
          'Recognition route available for products approved by ICH regulatory authorities (US FDA, EMA) since Dec 2022',
          'Traditional dossier route requires quality, safety and efficacy data',
          'Latin-America CMC review confirms requirements often align with CTD modules',
          'Administrative forms, power of attorney, local representative licence required',
          'Dossier: formula, manufacturing process, stability data, GMP certificate',
          'Spanish labelling mandatory; may include local reference studies',
          'Recognition route: evidence of foreign approval, product equivalence, streamlined manufacturing inspections',
          'GMP certificate from manufacturing site required',
          'Sanitary registration validity with renewal/variation requirements per local regulation',
          'Natural medicinal product regulations specify renewal rules under RTCA framework'
        ],
        insight: 'The December 2022 recognition decree is a game-changer for products with US FDA or EMA approval. Companies can leverage existing approvals for faster Costa Rica registration, though local documentation and Spanish labelling remain required. This positions Costa Rica as an attractive entry point for quality exporters with credible reference-market approvals.'
      },
      nutra: {
        timeline: 'Classification-dependent. Products correctly classified as dietary supplements or natural medicinal products require registration but may have different pathways and timelines than pharmaceutical medicines. Planning for several months is recommended; timeline depends on documentation quality and product complexity.',
        requirements: [
          'Dietary supplements and natural medicinal products must be registered before marketing',
          'Central American Technical Regulation RTCA 11.03.64 defines registration requirements for natural medicinal products',
          'Clear classification essential: food supplement vs natural medicinal product vs pharmaceutical',
          'Composition, dosage, permitted claims and manufacturing quality must meet RTCA/Ministry requirements',
          'Spanish labelling mandatory with required legends, dosage, warnings',
          'Claims must not imply disease treatment unless registered as medicine',
          'Free Sale Certificate from country of origin often required',
          'Manufacturing certificates (GMP or equivalent) required',
          'Product registration/import permit documentation',
          'Composition and dosage details per RTCA 11.03.64',
          'Claims aligned with permitted category (nutritional support, not therapeutic)'
        ],
        insight: 'Costa Rica follows Central American harmonised frameworks (RTCA) for natural products and supplements. Clear upfront classification is critical - mis-classification causes delays and potential rejection. Products with disease-treatment claims will be pushed into pharmaceutical category requiring full medicine registration. Work with evidence-backed nutritional claims and transparent ingredient disclosure.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in Costa Rica (Recognition / CTD-Aligned)',
      steps: [
        'Local Agent / MAH - Appoint legal representative or local importer authorised by MoH',
        'Determine Route - Recognition route (ICH approval) or traditional dossier route',
        'Recognition Route (if eligible) - Product approved by ICH regulatory authority (US FDA, EMA)',
        'Traditional Route - Full dossier with quality, safety and efficacy data aligned with CTD principles',
        'Administrative Documentation - Application forms, power of attorney, local representative licence',
        'Quality Dossier - Formula, manufacturing process, stability data, specifications',
        'GMP Certificate - From manufacturing site, recognised by Costa Rica authorities',
        'Foreign Approval Evidence - For recognition route: proof of ICH-market approval',
        'Product Equivalence - Demonstrate equivalence between approved product and Costa Rica submission',
        'Spanish Labelling - Patient information leaflet, outer packaging, primary labels in Spanish',
        'Local Reference Studies - May be required depending on product and pathway',
        'MoH Assessment - Technical evaluation; recognition route offers streamlined review',
        'Sanitary Registration - Certificate issued upon approval with defined validity period',
        'Renewal & Variations - Maintain registration through periodic renewals per local regulation'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Health Products in Costa Rica',
      steps: [
        'Classification Check - Dietary supplement vs natural medicinal product vs pharmaceutical',
        'RTCA Framework - Products fall under Central American Technical Regulation RTCA 11.03.64',
        'Registration Requirement - Must be registered before marketing in Costa Rica',
        'Composition Details - Full disclosure per RTCA 11.03.64 requirements',
        'Dosage & Claims - Permitted claims must align with nutritional support category',
        'Manufacturing Quality - GMP or equivalent certificates required',
        'Free Sale Certificate - From country of origin (often required)',
        'Spanish Labelling - Required legends, dosage instructions, warnings, ingredient list',
        'Claims Compliance - No disease-treatment implications unless registered as medicine',
        'Import Documentation - Product registration/permit for import',
        'Ministry Requirements - Meet RTCA/Ministry composition, dosage and quality standards',
        'Avoid Pharmaceutical Trigger - Ensure classification and claims do not move product into medicine category',
        'Registration with MoH - Obtain approval before first sale in Costa Rica'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals': [
        'Application form and power of attorney to local agent',
        'Local representative/importer licence',
        'GMP certificate from manufacturing site',
        'Evidence of foreign marketing approval (for recognition route)',
        'Quality dossier: formula, manufacturing process, stability data',
        'Spanish labelling: patient leaflet, outer packaging, primary labels',
        'Product equivalence documentation (for recognition pathway)',
        'Local reference studies (if required)',
        'Administrative and legal documentation',
        'Registration fees payment'
      ],
      'Nutraceuticals / Natural Medicinal Products': [
        'Composition and dosage details per RTCA 11.03.64',
        'Free Sale Certificate from country of origin',
        'Manufacturing certificates (GMP or equivalent)',
        'Product registration/import permit documentation',
        'Spanish labelling with required legends, dosage, warnings',
        'Claims documentation aligned with permitted category',
        'Proof product does not imply disease treatment',
        'Local importer/agent authorisation',
        'Quality documentation meeting RTCA/Ministry standards',
        'Import compliance documentation'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Costa Rica Belongs in Your Central-America Strategy',
      reasons: [
        'Favourable Regulatory Innovations - Recognition of ICH approvals simplifies pharma entry for credible dossiers from major regulators',
        'Stable Economy & Healthcare - Facilitates credible launch and local presence; supports export-regional hub strategy',
        'Manageable Market Size - Regulatory predictability enables brand recognition building before scaling to larger LatAm markets',
        'Central American Gateway - Entry positions you for expansion into Guatemala, El Salvador, Honduras, Nicaragua',
        'RTCA Harmonisation - Cross-market efficiencies through Central American Technical Regulations',
        'Quality-Focused Market - Upper-middle income population with high healthcare access for the region',
        'Strategic Location - Ideal launch pad for broader Central America and Caribbean penetration'
      ],
      bestPortfolio: [
        'Pharma: Generics and branded medicines with reference-market approvals (US FDA/EMA) or strong dossiers; chronic-care therapies (diabetes, hypertension, lipid-control, cardio-renal)',
        'Nutra/Wellness: Premium supplements for metabolic health, cardiovascular support, vitamins/minerals; natural medicinal products with correct classification and evidence-backed nutritional claims'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Costa Rica',
      services: [
        'Portfolio & Disease-Fit Mapping - Determine which products match Costa Rica NCD burden and entry strategy with recognition vs full dossier route',
        'Recognition Route Strategy - Leverage ICH-market approvals, prepare documentation to meet recognition decree, coordinate local representative and dossier adaptation',
        'Supplement & Natural-Product Classification - Advise on correct category, support manufacturing documentation, composition review, Spanish labelling, claims audit',
        'CTD/RTCA-Aligned Dossier Development - Prepare quality dossiers meeting Costa Rica and Central American technical requirements',
        'Local Agent / Importer Setup - Assist in selecting legal representative, distributor import licence, registration compliance and first-market launch',
        'Spanish Localisation - Translate and adapt all labelling, patient information leaflets and regulatory documentation',
        'Regional Hub Planning - Use Costa Rica dossier and registration assets for neighbouring Central American markets under RTCA harmonised frameworks'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines and supplements in Costa Rica?',
        answer: 'The Ministerio de Salud (MoH) is the regulatory authority for medicines, natural medicinal products, food supplements and health-products in Costa Rica.'
      },
      {
        question: 'Does Costa Rica recognise approvals from major regulators?',
        answer: 'Yes. From December 2022 the MoH recognizes marketing approvals granted by ICH regulatory authorities (such as US FDA and EMA) for pharmaceuticals, enabling streamlined registration through the recognition pathway.'
      },
      {
        question: 'How are dietary supplements regulated?',
        answer: 'Supplements and natural medicinal products must be registered before sale. Classification is important and they must comply with the RTCA 11.03.64 regulation for natural medicinal products, which is harmonised across Central America.'
      },
      {
        question: 'What are typical timelines for registration?',
        answer: 'Timelines depend on whether the recognition route is used (which may be faster for ICH-approved products) or full dossier route is required. Planning for several months is prudent; dossier quality and regulator workload significantly impact timelines.'
      },
      {
        question: 'Can Costa Rica act as a regional hub?',
        answer: 'Yes. Because of its regulatory alignment, stable market and RTCA harmonisation benefits, Costa Rica can serve as an effective launch pad into Central American markets using the same dossier assets for Guatemala, El Salvador, Honduras and Nicaragua.'
      }
    ]
  };

  return <CountryTemplate countryData={costaRicaData} />;
};
